import { DPO_EMAIL } from "@Common/constants";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { Box, Container, Typography } from "@mui/material";

export const PrivacyPage = () => {
	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
			<PublicNav />

			<Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 12 } }}>
				<Container maxWidth="md">
					<Typography
						variant="h1"
						sx={{
							fontSize: { xs: "2.5rem", md: "3.5rem" },
							fontWeight: 700,
							color: "text.primary",
							mb: 2,
							textAlign: "center",
						}}
					>
						Privacy Policy
					</Typography>

					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
							mb: 6,
							textAlign: "center",
						}}
					>
						Last updated: {new Date().toLocaleDateString()}
					</Typography>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Introduction
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							NoMachine ("we," "our," or "us") is committed to protecting your
							privacy. This Privacy Policy explains how we collect, use,
							disclose, and safeguard your information when you use our service.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Information We Collect
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We collect information that you provide directly to us, including:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Account information (name, email, password)
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Assessment data (questions, responses, scores)
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Usage information (how you interact with our service)
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Device and browser information
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							How We Use Your Information
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We use the information we collect to:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Provide, maintain, and improve our services
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Process your assessments and generate results
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Communicate with you about your account and our services
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Detect and prevent fraud and abuse
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Comply with legal obligations
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Data Security
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We implement appropriate technical and organizational measures to
							protect your information against unauthorized access, alteration,
							disclosure, or destruction. This includes:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Encryption of data in transit and at rest
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Regular security audits and assessments
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Access controls and authentication measures
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Employee training on data protection
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Information Sharing
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We do not sell your personal information. We may share your
							information only in the following circumstances:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									With your consent or at your direction
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									With service providers who assist in our operations
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									To comply with legal obligations
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									To protect our rights and prevent fraud
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Your Rights
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							You have the right to:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Access and receive a copy of your personal data
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Correct inaccurate or incomplete information
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Delete your personal data
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Object to or restrict certain processing
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Data portability
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Changes to This Policy
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We may update this Privacy Policy from time to time. We will
							notify you of any changes by posting the new Privacy Policy on
							this page and updating the "Last updated" date.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Contact Us
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								lineHeight: 1.8,
							}}
						>
							If you have questions about this Privacy Policy, please contact us
							at {DPO_EMAIL}.
						</Typography>
					</Box>
				</Container>
			</Box>

			<PublicFooter />
		</Box>
	);
};
